import type { ComponentType } from "react";
import { AwsMark } from "@/components/brand/aws-mark";
import { GithubBrand } from "@/components/icons/brand";
import {
  Activity,
  ArrowLeftRight,
  AudioLines,
  Bell,
  BookOpen,
  Bot,
  Boxes,
  Building2,
  Cable,
  CalendarClock,
  ChartNoAxesColumn,
  Container,
  Cpu,
  DollarSign,
  FileSpreadsheet,
  FileText,
  FlaskConical,
  FolderClosed,
  History,
  House,
  KeyRound,
  Layers3,
  LayoutDashboard,
  LifeBuoy,
  LineChart,
  MessageSquare,
  MessageSquareCode,
  OctagonAlert,
  Percent,
  Plug,
  ReceiptText,
  Repeat,
  Rocket,
  Route,
  Server,
  Shield,
  ShieldAlert,
  ShieldCheck,
  TrendingUp,
  UserPlus,
  UsersRound,
  Wallet,
} from "lucide-react";

import type { ClientCapability } from "./portal-routes";
import { consolePath, DEVELOPER_DOCS_URL, DEVELOPER_GUIDE_ROUTES, GLOBAL_ROUTES, orgPath } from "./portal-routes";

/**
 * The portal sidebar model (portal IA overhaul, 2026-09): one taxonomy for
 * every role, resolved against who is looking and where.
 *
 *   Home · My Resources · Inference · Compute · AWS Hub (Overview, Secrets Manager) · Lab · Chat ·
 *   Developers · Observe · Billing (admins) · Transfer (admins) ·
 *   Organization · Help
 *
 * Members and enterprise admins get the same sections and pages — an admin
 * just sees more entries (Router, Event Log, Activity, Privacy & Data) and more
 * controls on shared pages. Super admins have no organization: outside a
 * client console they see the Platform sections instead, and inside one the
 * same org taxonomy rebased onto `/admin/clients/<id>/…`.
 *
 * Pure and dependency-light so it is unit-testable; `components/portal-sidebar.tsx`
 * renders it.
 */

export type PortalRole = "member" | "enterprise_admin" | "super_admin";

/**
 * Narrow a Firebase custom-claim role to the sidebar's role vocabulary.
 * Lives here (a plain module, no "use client") because AuthenticatedContent,
 * a server component, calls it at render time: a function exported from a
 * client module is a client reference on the server and calling it throws
 * "Attempted to call portalRoleOf() from the server", which took every
 * (main) route down on the first UAT deploy of the portal IA overhaul.
 */
export function portalRoleOf(role: unknown): PortalRole {
  if (role === "super_admin" || role === "enterprise_admin") return role;
  return "member";
}

/** Lucide icons and the MCP mark share this shape. */
export type NavIcon = ComponentType<{ className?: string }>;

export type PortalNavScope =
  /** The viewer's own organization (members, enterprise admins). */
  | { kind: "org" }
  /** A super admin inside one client's console. */
  | { kind: "console"; clientId: string; clientName: string | null }
  /** A super admin on platform pages (or anywhere without a client). */
  | { kind: "platform" };

export interface PortalNavContext {
  role: PortalRole;
  scope: PortalNavScope;
  /** The org has web chat turned on (independent of which host serves it). */
  chatEnabled: boolean;
  /** Where "Open chat" goes: "/chat", or the absolute chat-host URL when the split is active. */
  chatHref: string;
  /** Realtime or custom voices enabled for the org. */
  voiceEnabled: boolean;
  /** Transfer › Transfer: the FUNDING_CODES_ENABLED switch is on. */
  fundingCodesEnabled: boolean;
  /** Transfer › Invite: the CLIENT_INVITES_ENABLED switch is on. */
  clientInvitesEnabled: boolean;
}

type Visibility = "all" | "admin";

interface ItemDef {
  id: string;
  label: string;
  icon: NavIcon;
  /** Client-scoped capability — href follows the scope (org or console). */
  capability?: ClientCapability;
  /** Fixed href: per-user or platform pages, and external links. */
  href?: string;
  external?: boolean;
  visibility?: Visibility;
  /** Hidden unless the org has the capability. */
  requires?: "chat" | "voice" | "fundingCodes" | "clientInvites";
  /** Extra path prefixes that keep the entry active (e.g. serverless under Models). */
  activePrefixes?: string[];
  /** Client-scoped sibling routes that should keep this entry active. */
  activeCapabilities?: ClientCapability[];
  /** Match the href exactly instead of by prefix (index routes). */
  exact?: boolean;
  /** Per-user entries make no sense inside a client console — hide them there. */
  userOnly?: boolean;
  /** Client-scoped entries make no sense on the platform-wide sidebar. */
  clientOnly?: boolean;
}

interface SectionDef {
  id: string;
  label: string;
  /** A single top-level link, not a collapsible parent with children. */
  standalone?: boolean;
  items: ItemDef[];
}

export interface PortalNavItem {
  id: string;
  label: string;
  icon: NavIcon;
  href: string | null;
  external: boolean;
  exact: boolean;
  activePrefixes: string[];
}

export interface PortalNavSection {
  id: string;
  label: string;
  standalone: boolean;
  items: PortalNavItem[];
}

const ORG_SECTIONS: SectionDef[] = [
  {
    id: "home",
    label: "Home",
    standalone: true,
    items: [{ id: "dashboard", label: "Home", icon: House, capability: "dashboard", exact: true }],
  },
  {
    // Everything the org is running (machines, dedicated endpoints, capacity
    // requests), one management page right under Home. The GPU page deploys
    // and only glances at these.
    id: "resources",
    label: "My Resources",
    standalone: true,
    items: [{ id: "resources", label: "My Resources", icon: Server, capability: "resources" }],
  },
  {
    id: "inference",
    label: "Inference",
    items: [
      {
        id: "models",
        label: "Models",
        icon: Boxes,
        capability: "models",
        exact: true,
        activePrefixes: ["/gpu/serverless"],
      },
      // The rate card is for everyone who can pick a model, not just admins:
      // a member choosing between models needs the price next to the choice.
      { id: "pricing", label: "Pricing", icon: TrendingUp, capability: "pricing" },
      { id: "router", label: "Router", icon: Route, capability: "routing", visibility: "admin" },
      { id: "voice", label: "Voice", icon: AudioLines, capability: "voice", requires: "voice" },
    ],
  },
  {
    // The GPU workspace is the one Compute page: deploy (count and memory
    // sliders → GPU models → offers). Management is My Resources above.
    id: "compute",
    label: "Compute",
    items: [
      // Exact because Dedicated deployments and RunPod are sibling products
      // under /gpu and must each have one active navigation entry.
      { id: "gpu", label: "GPUs", icon: Cpu, capability: "gpu", exact: true },
      {
        id: "dedicated-deployments",
        label: "Dedicated deployments",
        icon: Rocket,
        capability: "endpoints",
      },
      // The org's assigned RunPod organization, its credentials and the
      // client-connection snippets (AIR-1554).
      { id: "runpod", label: "RunPod", icon: Container, capability: "runpod" },
    ],
  },
  {
    // The AWS Hub (AIR-1616): the org's own AWS account — how a coding agent
    // and the AWS CLI connect, the account, the federated console, Secrets
    // Manager. A collapsible section under the AWS mark (2026-10-23: as one
    // bare entry it was easy to miss) with its two tabs as entries. Overview
    // is exact plus the ECS workspace prefix (`/aws/ecs` is a detail surface
    // of the Overview, reached from My Resources), Secrets Manager is its own
    // path; My Resources › AWS and Billing › AWS Billing stay where they are.
    id: "aws",
    label: "AWS Hub",
    items: [
      { id: "aws", label: "Overview", icon: AwsMark, capability: "aws", exact: true, activeCapabilities: ["containers"] },
      { id: "secrets-manager", label: "Secrets Manager", icon: KeyRound, capability: "secrets" },
    ],
  },
  {
    id: "lab",
    label: "Lab",
    // Lab lists only shipped capabilities; a capability joins the day it is live.
    items: [{ id: "training", label: "Training", icon: Layers3, capability: "training" }],
  },
  {
    id: "chat",
    label: "Chat",
    items: [
      // Not userOnly: inside a client console the link still opens the
      // viewer's own chat, and an admin working an org's setup wants it one
      // click away (2026-10-22). The console never has an org chat of its own
      // to link to — chat is per viewer, so this is the only sensible target.
      { id: "open-chat", label: "Open Chat", icon: MessageSquare, requires: "chat" },
      { id: "tools", label: "MCP & Tools", icon: Cable, href: GLOBAL_ROUTES.tools, userOnly: true },
      { id: "documents", label: "Documents", icon: FileText, href: GLOBAL_ROUTES.documents, userOnly: true },
      {
        id: "context",
        label: "Context",
        icon: FolderClosed,
        capability: "context",
        visibility: "admin",
      },
    ],
  },
  {
    id: "developers",
    label: "Developers",
    items: [
      { id: "quickstart", label: "Quickstart", icon: Rocket, href: GLOBAL_ROUTES.quickstart, exact: true },
      // The sandbox: try models and copy the call before writing any code.
      // Per viewer (nothing stored), so it is a fixed href on every scope,
      // the platform sidebar included — super admins test models here too.
      { id: "playground", label: "Playground", icon: MessageSquareCode, href: GLOBAL_ROUTES.playground, exact: true },
      // Coding Agents is exact so its index page, not every tool guide
      // beneath it, lights the entry; GitHub Copilot gets its own entry
      // because customers look for it by name (Ashwin, 2026-10-19) and the
      // other guides stay reachable from the Coding Agents page.
      {
        id: "quickstart-agents",
        label: "Coding Agents",
        icon: Bot,
        href: GLOBAL_ROUTES.quickstartCodingAgents,
        exact: true,
      },
      { id: "quickstart-copilot", label: "GitHub Copilot", icon: GithubBrand, href: DEVELOPER_GUIDE_ROUTES.copilot },
    ],
  },
  {
    id: "observe",
    label: "Observe",
    items: [
      { id: "event-log", label: "Event Log", icon: ReceiptText, capability: "eventLog", visibility: "admin" },
      { id: "analytics", label: "Analytics", icon: ChartNoAxesColumn, capability: "analytics" },
      { id: "activity", label: "Activity", icon: History, capability: "activity", visibility: "admin" },
    ],
  },
  {
    id: "billing",
    label: "Billing",
    items: [
      {
        id: "financials",
        label: "Financials",
        icon: Wallet,
        capability: "wallet",
        exact: true,
        activeCapabilities: ["walletCategories"],
        visibility: "admin",
      },
      {
        id: "aws-billing",
        label: "AWS Billing",
        icon: AwsMark,
        capability: "awsBilling",
        visibility: "admin",
      },
    ],
  },
  {
    id: "transfer",
    label: "Transfer",
    items: [
      {
        id: "invite",
        label: "Invite",
        icon: UserPlus,
        capability: "walletInvite",
        visibility: "admin",
        requires: "clientInvites",
      },
      {
        id: "transfer",
        label: "Transfer",
        icon: ArrowLeftRight,
        capability: "walletTransfer",
        visibility: "admin",
        requires: "fundingCodes",
      },
    ],
  },
  {
    id: "organization",
    label: "Organization",
    items: [
      { id: "members", label: "Members", icon: UsersRound, capability: "org" },
      { id: "keys", label: "API Keys", icon: KeyRound, capability: "keys" },
      { id: "notifications", label: "Notifications", icon: Bell, capability: "notifications" },
      {
        id: "privacy",
        label: "Privacy & Data",
        icon: Shield,
        capability: "privacy",
        visibility: "admin",
      },
    ],
  },
  {
    id: "help",
    label: "Help",
    items: [
      { id: "docs", label: "Docs", icon: BookOpen, href: DEVELOPER_DOCS_URL, external: true },
      { id: "support", label: "Support", icon: LifeBuoy, href: GLOBAL_ROUTES.help },
      {
        id: "integrations",
        label: "Integrations",
        icon: Plug,
        capability: "integrations",
        clientOnly: true,
      },
    ],
  },
];

/** Super-admin platform tooling — never in a member's or enterprise admin's sidebar. */
export const PLATFORM_SECTIONS: PortalNavSection[] = [
  {
    id: "platform",
    label: "Platform",
    standalone: false,
    items: [
      { id: "admin", label: "Dashboard", icon: LayoutDashboard, href: "/admin", exact: true },
      { id: "clients", label: "Clients", icon: Building2, href: "/admin/clients" },
      // Platform-wide usage and spend across every organization, then the
      // failures behind them; the Ledger is the request-level explorer both
      // deep-link into.
      { id: "platform-analytics", label: "Analytics", icon: ChartNoAxesColumn, href: "/admin/analytics" },
      { id: "platform-errors", label: "Errors", icon: OctagonAlert, href: "/admin/errors" },
      // Who signs in to the web app, how long they stay, which pages they
      // use (2026-10-15, go-to-market) — the product-usage counterpart of
      // the API-usage Analytics above.
      { id: "engagement", label: "Engagement", icon: UsersRound, href: "/admin/engagement" },
      { id: "usage", label: "Ledger", icon: LineChart, href: "/admin/litellm" },
      { id: "internal-calls", label: "Internal Calls", icon: Activity, href: "/admin/internal-calls" },
      // Generic label on purpose (AIR-954): the surface monitors policy-gated
      // model access without naming the models anywhere discoverable.
      { id: "gated-models", label: "Gated Models", icon: ShieldAlert, href: "/admin/adult-content" },
      { id: "provider-spend", label: "Provider Spend", icon: DollarSign, href: "/admin/provider-spend" },
      { id: "internal-spend", label: "Internal Spend", icon: Wallet, href: "/admin/internal-spend" },
      // Recurring wallet top-ups across every client (set up per client on
      // its Wallet page; this is the platform-wide register).
      { id: "allocations", label: "Scheduled Allocations", icon: Repeat, href: "/admin/allocations" },
      { id: "cogs", label: "COGS & Margin", icon: Percent, href: "/admin/cogs" },
      { id: "pricing", label: "Pricing", icon: TrendingUp, href: "/admin/pricing" },
      { id: "cache-rates", label: "Cache Rates", icon: FlaskConical, href: "/admin/cache-rates" },
      { id: "deprecations", label: "Deprecations", icon: CalendarClock, href: "/admin/model-deprecations" },
      { id: "carr", label: "CARR Reports", icon: FileSpreadsheet, href: "/admin/carr" },
      { id: "system-users", label: "Admin Users", icon: ShieldCheck, href: "/admin/system-users" },
    ].map((item) => ({
      ...item,
      external: false,
      exact: item.exact ?? false,
      activePrefixes: [],
    })),
  },
];

/** Sections that carry no client scope — shown to super admins alongside the Platform list. */
const GLOBAL_SECTION_IDS = new Set(["developers", "help"]);

function isAdminRole(role: PortalRole): boolean {
  return role !== "member";
}

function resolveItem(def: ItemDef, ctx: PortalNavContext): PortalNavItem | null {
  if (def.visibility === "admin" && !isAdminRole(ctx.role)) return null;
  if (def.requires === "chat" && !ctx.chatEnabled) return null;
  if (def.requires === "voice" && !ctx.voiceEnabled) return null;
  if (def.requires === "fundingCodes" && !ctx.fundingCodesEnabled) return null;
  if (def.requires === "clientInvites" && !ctx.clientInvitesEnabled) return null;
  if (def.userOnly && ctx.scope.kind !== "org") return null;
  if (def.clientOnly && ctx.scope.kind === "platform") return null;

  let href: string | null = def.href ?? null;
  if (def.id === "open-chat") href = ctx.chatHref;
  if (def.capability) {
    href = ctx.scope.kind === "console" ? consolePath(ctx.scope.clientId, def.capability) : orgPath(def.capability);
  }

  return {
    id: def.id,
    label: def.label,
    icon: def.icon,
    href,
    external: def.external ?? false,
    exact: def.exact ?? false,
    activePrefixes: [
      ...(def.activePrefixes ?? []),
      ...(def.activeCapabilities ?? []).map((capability) =>
        ctx.scope.kind === "console" ? consolePath(ctx.scope.clientId, capability) : orgPath(capability),
      ),
    ],
  };
}

/**
 * The sidebar for one viewer. Empty sections are dropped so a member never
 * sees a heading with nothing under it.
 */
export function buildPortalNav(ctx: PortalNavContext): PortalNavSection[] {
  if (ctx.scope.kind === "platform") {
    const globals = ORG_SECTIONS.filter((section) => GLOBAL_SECTION_IDS.has(section.id));
    return [...PLATFORM_SECTIONS, ...resolveSections(globals, ctx)];
  }
  return resolveSections(ORG_SECTIONS, ctx);
}

function resolveSections(sections: SectionDef[], ctx: PortalNavContext): PortalNavSection[] {
  return sections
    .map((section) => ({
      id: section.id,
      label: section.label,
      standalone: section.standalone ?? false,
      items: section.items.map((item) => resolveItem(item, ctx)).filter((item): item is PortalNavItem => item !== null),
    }))
    .filter((section) => section.items.length > 0);
}

/** Whether `pathname` belongs to `item` (exact for index routes, prefix otherwise). */
export function isNavItemActive(
  item: Pick<PortalNavItem, "href" | "exact" | "activePrefixes" | "external">,
  pathname: string,
): boolean {
  if (!item.href || item.external) return false;
  if (item.activePrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) return true;
  if (item.exact) return pathname === item.href;
  return pathname === item.href || pathname.startsWith(`${item.href}/`);
}
